import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play } from "lucide-react";
import { api } from "../lib/api";
import { useI18n } from "../i18n/I18nProvider";
import Reveal from "../components/Reveal";
import VideoLightbox from "../components/VideoLightbox";
import SEO from "../components/SEO";
import { getSiteOrigin } from "../siteOrigin";

const FILTER_KEYS = ["all", "video", "photo", "drone", "social", "branding"];

export default function PortfolioPage() {
  const { t, lang } = useI18n();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [lightbox, setLightbox] = useState({ open: false, url: null, title: null });

  useEffect(() => {
    api.get("/portfolio").then((r) => setItems(r.data || [])).catch(() => {});
  }, []);

  const filtered = useMemo(() => filter === "all" ? items : items.filter((i) => i.category === filter), [filter, items]);

  const schema = useMemo(() => {
    if (!items.length) return null;
    const SITE = getSiteOrigin();
    const abs = (p) => (p?.startsWith("http") ? p : `${SITE}${p || ""}`);
    const breadcrumb = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: lang === "fr" ? "Accueil" : "Home", item: `${SITE}/` },
        { "@type": "ListItem", position: 2, name: "Portfolio", item: `${SITE}/portfolio` },
      ],
    };
    const itemList = {
      "@context": "https://schema.org",
      "@type": "ItemList",
      itemListElement: items.map((p, i) => ({
        "@type": "ListItem",
        position: i + 1,
        item: {
          "@type": p.video_url ? "VideoObject" : "CreativeWork",
          name: lang === "fr" ? p.title_fr : p.title,
          description: lang === "fr" ? p.summary_fr : p.summary,
          thumbnailUrl: abs(p.cover),
          contentUrl: p.video_url ? abs(p.video_url) : undefined,
          uploadDate: `${p.year}-01-01`,
          inLanguage: lang === "fr" ? "fr-CA" : "en-CA",
        },
      })),
    };
    return [breadcrumb, itemList];
  }, [items, lang]);

  const openVideo = (item) => {
    if (!item.video_url) return;
    setLightbox({ open: true, url: item.video_url, title: lang === "fr" ? item.title_fr : item.title });
  };

  return (
    <main className="pt-40 lg:pt-48 pb-32 bg-[#050505] min-h-screen" data-testid="page-portfolio">
      <SEO
        title={lang === "fr"
          ? "Portfolio — Films de Marque, Photo & Drone | Moncton NB"
          : "Portfolio — Brand Films, Photography & Drone | Moncton NB"}
        description={lang === "fr"
          ? "Découvrez les films de marque, portraits éditoriaux et travaux aériens de Phaneroo Prod, studio cinématique bilingue à Moncton, NB. Clients du Nouveau-Brunswick, Nouvelle-Écosse et Canada Atlantique."
          : "Discover brand films, editorial portraits and drone aerial work from Phaneroo Prod, bilingual cinematic studio in Moncton, NB. Clients across New Brunswick, Nova Scotia and Atlantic Canada."}
        path="/portfolio"
        lang={lang}
        image={items[0]?.cover}
        schema={schema}
      />
      <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
        <Reveal>
          <div className="flex items-center gap-3 text-[11px] tracking-[0.28em] uppercase text-ink-300">
            <span className="block w-8 h-px bg-rouge" /><span>{t.portfolio.eyebrow}</span>
          </div>
          <h1 className="mt-6 font-serif-editorial text-5xl sm:text-6xl lg:text-7xl text-ink-100 tracking-tighter leading-[1.02] max-w-4xl">
            {t.portfolio.title}
          </h1>
        </Reveal>

        <div className="mt-12 mb-10 flex flex-wrap items-center gap-2 lg:gap-3 border-t border-hair pt-8">
          {FILTER_KEYS.map((k) => (
            <button
              key={k}
              onClick={() => setFilter(k)}
              data-testid={`portfolio-filter-${k}`}
              data-cursor={t.portfolio.filter[k]}
              className={`px-4 py-2 text-[11px] tracking-[0.22em] uppercase border transition-colors ${
                filter === k ? "border-rouge text-ink-100 bg-rouge/10" : "border-hair text-ink-300 hover:text-ink-100 hover:border-ink-500"
              }`}
            >
              {t.portfolio.filter[k]}
            </button>
          ))}
        </div>

        <AnimatePresence mode="popLayout">
          <motion.div
            key={filter}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="columns-1 sm:columns-2 lg:columns-3 gap-6"
          >
            {filtered.map((p, i) => {
              const playable = !!p.video_url;
              return (
                <motion.article
                  key={p.id}
                  layout
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.7, delay: (i % 6) * 0.05, ease: [0.16, 1, 0.3, 1] }}
                  className="break-inside-avoid mb-6 group relative overflow-hidden bg-[#0c0c0e]"
                  data-testid={`portfolio-card-${i}`}
                >
                  <button
                    type="button"
                    onClick={() => playable && openVideo(p)}
                    data-cursor={playable ? "PLAY" : p.category.toUpperCase()}
                    data-testid={`portfolio-play-${p.slug}`}
                    className={`block w-full text-left ${playable ? "cursor-pointer" : ""}`}
                  >
                    <div className={`relative ${p.aspect === "portrait" ? "aspect-[3/4]" : p.aspect === "square" ? "aspect-square" : "aspect-[16/10]"}`}>
                      <img
                        src={p.cover}
                        alt={lang === "fr" ? p.title_fr : p.title}
                        loading="lazy"
                        className="absolute inset-0 w-full h-full object-cover transition-all duration-[1100ms] saturate-50 group-hover:saturate-100 group-hover:scale-[1.04]"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/30 to-transparent" />
                      {playable && (
                        <div className="absolute top-4 left-4 flex items-center gap-2 bg-black/60 backdrop-blur px-2.5 py-1.5 border border-white/10">
                          <Play size={11} className="text-rouge fill-rouge" />
                          <span className="text-[10px] tracking-[0.22em] uppercase text-ink-100">Play</span>
                        </div>
                      )}
                      <div className="absolute inset-0 p-6 flex flex-col justify-end">
                        <div className="text-[10px] tracking-[0.3em] uppercase text-ink-300 mb-2">{p.category} · {p.year}</div>
                        <h2 className="font-serif-editorial text-2xl lg:text-3xl text-ink-100 leading-tight">
                          {lang === "fr" ? p.title_fr : p.title}
                        </h2>
                        {p.client && <div className="mt-2 text-ink-500 text-xs">{p.client}</div>}
                      </div>
                    </div>
                  </button>
                </motion.article>
              );
            })}
          </motion.div>
        </AnimatePresence>

        {!filtered.length && (
          <div className="text-ink-500 text-sm py-20 text-center" data-testid="portfolio-empty">
            {lang === "fr" ? "Aucun projet dans cette catégorie pour l'instant." : "No projects in this category yet."}
          </div>
        )}
      </div>

      <VideoLightbox
        open={lightbox.open}
        videoUrl={lightbox.url}
        title={lightbox.title}
        onClose={() => setLightbox({ open: false, url: null, title: null })}
      />
    </main>
  );
}
